<template>
    <v-container fluid style="background-color: white; height: inherit;">

        <CenteredLoading v-if="isLoading && !initiated" :displayBool="true">
        </CenteredLoading>

        <div v-else-if="!configuration || !Object.keys(configuration).length">
            AI settings are not available for you.
        </div>

        <div v-else>
            <!-- Enable / Disable master switch -->
            <v-card flat class="mb-4 pa-4 d-flex align-center" style="border: 1px solid #e0e0e0; border-radius: 12px;">
                <v-icon :color="isEnabled ? 'primary' : 'grey'" size="28" class="mr-3">
                    {{ isEnabled ? 'mdi-robot-happy-outline' : 'mdi-robot-off-outline' }}
                </v-icon>
                <div class="flex-grow-1">
                    <div class="text-subtitle-1 font-weight-medium">
                        AI Assistant
                    </div>
                    <div class="text-caption grey--text">
                        {{ isEnabled ? 'AI features are active and available to users' : 'AI features are currently turned off' }}
                    </div>
                </div>
                <v-switch v-model="isEnabled" :loading="isLoading" inset hide-details color="primary"
                    class="mt-0 pt-0" @change="onToggleEnabled" />
            </v-card>

            <!-- Disabled placeholder -->
            <v-fade-transition>
                <v-card v-if="!isEnabled" flat class="text-center py-16 px-8"
                    style="border: 2px dashed #ccc; border-radius: 16px; background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);">
                    <v-icon size="96" color="grey lighten-1" class="mb-4">mdi-robot-off-outline</v-icon>
                    <div class="text-h5 grey--text text--darken-1 mb-2">AI is Disabled</div>
                    <div class="text-body-1 grey--text mb-6" style="max-width: 480px; margin: 0 auto;">
                        Turn on the switch above to configure AI agents, providers, and channels for your organization.
                    </div>
                    <v-btn outlined color="primary" @click="isEnabled = true; onToggleEnabled(true);">
                        <v-icon left>mdi-power</v-icon>
                        Enable AI
                    </v-btn>
                </v-card>
            </v-fade-transition>

            <!-- Settings form -->
            <v-expand-transition>
                <v-form v-if="isEnabled" v-model="valid" :key="iteration">
                    <v-tabs v-model="activeTab">
                        <v-tab v-for="section in sections" :key="'tab-' + section.Name">
                            {{ section.Name }}
                        </v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="activeTab">
                        <v-tab-item v-for="section in sections" :key="'tab-item-' + section.Name">
                            <v-col class="pt-4">
                                <QAISettingsSection :section="section" :configuration="configuration"
                                    @update:configuration="onConfigurationUpdate" />

                                <v-row no-gutters class="mt-3">
                                    <v-btn :disabled="!saveable" :loading="isLoading"
                                        :color="saveable ? 'primary' : 'disabled'"
                                        @click="saveSectionConfiguration(section)">
                                        {{ $t('servicesystem.common_objects.commonterms.save') }}
                                    </v-btn>
                                </v-row>
                            </v-col>
                        </v-tab-item>
                    </v-tabs-items>
                </v-form>
            </v-expand-transition>
        </div>
    </v-container>
</template>


<script>
import clientAPI from "@/api/ClientAPIMixin";
import QInputRuleMixin from "@/helpers/Mixins/QInputRuleMixin";
import CenteredLoading from "@/components/Sections/CenteredLoading.vue";
import QAISettingsSection from "@/components/QAI/QAISettingsSection.vue";

export default {
    name: "QAISettings",
    components: {
        CenteredLoading,
        QAISettingsSection,
    },
    mixins: [clientAPI, QInputRuleMixin],

    data() {
        return {
            initiated: false,
            isLoading: false,
            configuration: {},
            definitions: {},
            iteration: 1,
            valid: true,
            activeTab: 0,
            isEnabled: false,
        };
    },

    mounted() {
        this.fetchConfiguration();
    },

    methods: {
        fetchConfiguration() {
            this.isLoading = true;
            clientAPI.methods.getQAISettings()
                .then(response => {
                    this.configuration = response.Configuration || {};
                    this.definitions = response.Definitions || {};
                    this.isEnabled = !!this.configuration.enabled;
                    this.iteration++;
                })
                .finally(() => {
                    this.isLoading = false;
                    this.initiated = true;
                });
        },

        getSectionConfigKey(section) {
            const SECTION_KEY_MAP = {
                Agents: "agents",
                Providers: "providers",
                Channels: "channels",
            };
            return SECTION_KEY_MAP[section.Name] || section.Name.toLowerCase();
        },

        async saveSectionConfiguration(section) {
            this.isLoading = true;
            const configKey = this.getSectionConfigKey(section);
            const sectionValue = this.configuration[configKey];

            const payload = {
                enabled: this.configuration.enabled,
                sections: [{
                    section: section.Name,
                    value: JSON.stringify(sectionValue)
                }],
            };

            await clientAPI.methods.saveQAISettings(payload)
                .then(response => {
                    this.configuration = response.Configuration || {};
                    this.definitions = response.Definitions || {};
                    this.iteration++;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        onConfigurationUpdate(updatedConfig) {
            this.configuration = updatedConfig;
        },

        async onToggleEnabled(val) {
            this.isLoading = true;
            this.configuration = { ...this.configuration, enabled: val };

            const payload = {
                enabled: val,
                sections: [],
            };

            await clientAPI.methods.saveQAISettings(payload)
                .then(response => {
                    this.configuration = response.Configuration || {};
                    this.definitions = response.Definitions || {};
                    this.isEnabled = !!this.configuration.enabled;
                    this.iteration++;
                })
                .catch(() => {
                    // Revert on failure
                    this.isEnabled = !val;
                    this.configuration = { ...this.configuration, enabled: !val };
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },

    computed: {
        sections() {
            return (this.definitions && this.definitions.Sections) || [];
        },

        saveable() {
            return this.initiated && !this.isLoading;
        },
    },

    watch: {},
};
</script>


<style lang="scss" scoped></style>